import React, { useState, useEffect } from 'react';
import { ClipboardList, Clock, CheckCircle, XCircle, Plus, Loader2, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { normalizeAppError } from '../../utils/apiErrors';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';


const SOLIC_STEPS = [
    { target: '[data-testid="btn-nova-solicitação"]', title: 'Nova Solicitacao', content: 'Solicite ajustes de ponto, justificativas ou correcoes. Sua empresa sera notificada automaticamente.' },
    { target: '[data-testid="colaborador-solicitações"]', title: 'Suas Solicitacoes', content: 'Acompanhe o status de cada pedido: pendente, aprovado ou recusado.' },
];

export default function ColaboradorSolicitaçõesPage() {
    const [ajustes, setAjustes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ motivo: '', descrição: '', data_ref: '' });
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetch(`${API}/ponto/ajustes`, { headers }).then(r => r.json())
            .then(data => { setAjustes(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const r = await fetch(`${API}/ponto/ajustes`, { method: 'POST', headers, body: JSON.stringify(form) });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || 'Erro ao enviar');
            toast.success('Solicitação enviada!');
            setShowForm(false);
            setForm({ motivo: '', descrição: '', data_ref: '' });
            setAjustes(prev => [data, ...prev]);
        } catch (err) { toast.error(normalizeAppError(err).message); }
        setSaving(false);
    };

    const counts = { pendente: ajustes.filter(a => a.status === 'pendente').length, aprovado: ajustes.filter(a => a.status === 'aprovado').length, recusado: ajustes.filter(a => a.status === 'recusado').length };

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-5" data-testid="colaborador-solicitações">
            <ContextualTour pageKey="solicitacoes" steps={SOLIC_STEPS} />
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
                {[
                    { label: 'Pendentes', value: counts.pendente, color: '#F59E0B' },
                    { label: 'Aprovadas', value: counts.aprovado, color: '#10B981' },
                    { label: 'Recusadas', value: counts.recusado, color: '#EF4444' },
                ].map((c) => (
                    <div key={c.label} className="rounded-xl p-3.5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <p className="text-xl font-bold" style={{ color: c.color }}>{c.value}</p>
                        <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{c.label}</p>
                    </div>
                ))}
            </div>

            {/* New request button */}
            <Button onClick={() => setShowForm(!showForm)} className="btn-gold text-sm w-full" data-testid="btn-nova-solicitação">
                <Plus size={14} className="mr-1" /> Nova Solicitação
            </Button>

            {/* Form */}
            {showForm && (
                <form onSubmit={handleSubmit} className="rounded-xl p-4 sm:p-5 space-y-3" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.3)' }} data-testid="form-solicitação">
                    <h4 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Nova Solicitação de Ajuste</h4>
                    <select value={form.motivo} onChange={e => setForm({...form, motivo: e.target.value})} required
                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                        <option value="">Selecione o motivo</option>
                        <option value="esqueci_marcar">Esqueci de marcar</option>
                        <option value="erro_sistema">Erro do sistema</option>
                        <option value="atraso_justificado">Atraso justificado</option>
                        <option value="ausência_justificada">Ausência justificada</option>
                        <option value="outro">Outro</option>
                    </select>
                    <input type="date" value={form.data_ref} onChange={e => setForm({...form, data_ref: e.target.value})} required
                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    <textarea value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} placeholder="Descreva o motivo..."
                        rows={3} className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    <div className="flex gap-2">
                        <Button type="submit" disabled={saving} className="btn-gold text-sm">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Send size={12} className="mr-1" /> Enviar</>}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="text-sm">Cancelar</Button>
                    </div>
                </form>
            )}

            {/* List */}
            {ajustes.length === 0 ? (
                <div className="text-center py-12 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <ClipboardList size={36} className="mx-auto mb-3" style={{ color: 'var(--muted)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhuma solicitação</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Suas solicitações de ajuste aparecerao aqui</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {ajustes.map((a) => {
                        const sc = { pendente: { color: '#F59E0B', icon: Clock }, aprovado: { color: '#10B981', icon: CheckCircle }, recusado: { color: '#EF4444', icon: XCircle } };
                        const s = sc[a.status] || sc.pendente;
                        const Icon = s.icon;
                        const motivoLabels = { esqueci_marcar: 'Esqueci de marcar', erro_sistema: 'Erro do sistema', atraso_justificado: 'Atraso justificado', ausência_justificada: 'Ausência justificada', outro: 'Outro' };
                        return (
                            <div key={a.ajuste_id || a._id} className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{motivoLabels[a.motivo] || a.motivo || 'Ajuste'}</p>
                                        {a.descricao && <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{a.descricao}</p>}
                                        <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>
                                            {a.data_ref || ''} {a.created_at ? `- ${new Date(a.created_at).toLocaleDateString('pt-BR')}` : ''}
                                        </p>
                                    </div>
                                    <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ background: `${s.color}12`, color: s.color }}>
                                        <Icon size={10} /> {a.status}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
